import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Edit2, Plus, X } from 'lucide-react';
import { apiGet, apiPatch, apiPost } from '../../lib/api';

const emptyForm = {
  name: '',
  price: 0,
  billing_cycle: 'monthly',
  customer_limit: '',
  staff_limit: '',
  features_text: '',
  status: 'active',
};

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

function toForm(plan) {
  return {
    name: plan?.name || '',
    price: plan?.price ?? 0,
    billing_cycle: plan?.billing_cycle || 'monthly',
    customer_limit: plan?.customer_limit ?? '',
    staff_limit: plan?.staff_limit ?? '',
    features_text: (plan?.features || []).join('\n'),
    status: plan?.status || 'active',
  };
}

function toPayload(form) {
  return {
    name: form.name.trim(),
    price: Number(form.price || 0),
    billing_cycle: form.billing_cycle || 'monthly',
    customer_limit: form.customer_limit === '' ? null : Number(form.customer_limit),
    staff_limit: form.staff_limit === '' ? null : Number(form.staff_limit),
    features: form.features_text.split('\n').map((f) => f.trim()).filter(Boolean),
    status: form.status || 'active',
  };
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => apiGet('/admin/plans').then(r => setPlans(r.data || [])).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const startEdit = (plan) => {
    setEditing(plan);
    setForm(toForm(plan));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Plan name is required');
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (editing?.id) {
        await apiPatch(`/admin/plans/${editing.id}`, payload);
        toast.success('Plan updated');
      } else {
        await apiPost('/admin/plans', payload);
        toast.success('Plan created');
      }
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (plan) => {
    const next = plan.status === 'active' ? 'inactive' : 'active';
    try {
      await apiPatch(`/admin/plans/${plan.id}`, { status: next });
      toast.success(next === 'active' ? 'Plan published' : 'Plan hidden from website');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="space-y-5" data-testid="admin-plans">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-rozops-navy">Subscription plans</h1>
          <p className="text-sm text-rozops-text-muted mt-1">Changes are stored in Supabase and active plans appear on the public pricing page.</p>
        </div>
        <button onClick={startCreate} data-testid="plan-new" className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New plan
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-5 items-start">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map(p => (
            <div key={p.id} className={`bg-white rounded-2xl border shadow-soft p-6 ${p.status !== 'active' ? 'border-gray-200 opacity-70' : 'border-gray-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-rozops-text-muted font-bold">{p.status || 'active'}</div>
                  <div className="mt-1 font-heading font-bold text-xl text-rozops-navy">{p.name}</div>
                </div>
                <button onClick={() => startEdit(p)} className="text-rozops-blue p-1.5" data-testid={`plan-edit-${p.name.toLowerCase()}`}><Edit2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 font-heading font-extrabold text-3xl text-rozops-navy">₹{Number(p.price || 0).toFixed(0)}</div>
              <div className="text-sm text-rozops-text-muted">/{p.billing_cycle || 'monthly'} · up to {p.customer_limit ?? 'unlimited'} customers · {p.staff_limit ?? 'unlimited'} staff</div>
              <div className="mt-4 space-y-1.5 min-h-[96px]">
                {(p.features || []).map((f) => <div key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-rozops-green mt-0.5 shrink-0" /> {f}</div>)}
              </div>
              <button onClick={() => toggleStatus(p)} className={`mt-4 text-xs font-bold rounded-lg px-3 py-2 ${p.status === 'active' ? 'bg-red-50 text-red-600' : 'bg-rozops-green-light text-rozops-green'}`}>
                {p.status === 'active' ? 'Hide from website' : 'Publish on website'}
              </button>
            </div>
          ))}
        </div>

        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 sticky top-6" data-testid="plan-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl text-rozops-navy">{editing ? 'Edit plan' : 'Create plan'}</h2>
            {editing && <button type="button" onClick={startCreate} className="text-rozops-text-muted"><X className="w-4 h-4" /></button>}
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Name</label>
              <input required className={inputCls} value={form.name} onChange={update('name')} data-testid="plan-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Price</label>
                <input type="number" min="0" step="0.01" className={inputCls} value={form.price} onChange={update('price')} data-testid="plan-price" />
              </div>
              <div>
                <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Cycle</label>
                <select className={inputCls} value={form.billing_cycle} onChange={update('billing_cycle')}>
                  <option value="monthly">monthly</option>
                  <option value="yearly">yearly</option>
                  <option value="custom">custom</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Customers</label>
                <input type="number" min="0" className={inputCls} value={form.customer_limit} onChange={update('customer_limit')} placeholder="Unlimited" />
              </div>
              <div>
                <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Staff</label>
                <input type="number" min="0" className={inputCls} value={form.staff_limit} onChange={update('staff_limit')} placeholder="Unlimited" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Status</label>
              <select className={inputCls} value={form.status} onChange={update('status')}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase font-bold text-rozops-text-muted mb-1">Features, one per line</label>
              <textarea rows={7} className={inputCls} value={form.features_text} onChange={update('features_text')} data-testid="plan-features" />
            </div>
            <button disabled={busy} data-testid="plan-save" className="w-full bg-rozops-blue text-white rounded-xl px-4 py-3 font-semibold disabled:opacity-50">
              {busy ? 'Saving...' : editing ? 'Save changes' : 'Create plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
